import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useLocation } from '@tanstack/react-router';
import { api } from 'shared/api/client';
import type { paths } from 'shared/api/generated/api';

import { commentKeys } from './keys';

type CreateCommentBody = NonNullable<paths['/comments/section/{sectionId}']['post']['requestBody']>['content']['application/json'];
type CommentData = CreateCommentBody['data'];

export const useComments = (sectionSuperId: string) => {
  const queryClient = useQueryClient();
  const { pathname } = useLocation();

  const {
    data: comments,
    isLoading: isCommentsLoading,
    refetch,
  } = useQuery({
    queryKey: commentKeys.list(sectionSuperId),

    queryFn: async () => {
      const { data } = await api.GET('/comments/section/{sectionId}', { params: { path: { sectionId: sectionSuperId } } });
      return data;
    },

    enabled: !!sectionSuperId && !pathname.includes('pdf'),
  });

  // NOTE: the legacy mutations passed `enabled:` — a no-op for react-query mutations; dropped during TS conversion (behavior unchanged).
  const createComment = useMutation({
    mutationKey: commentKeys.create(sectionSuperId),

    mutationFn: async ({ comment }: { comment: unknown }) => {
      const { data } = await api.POST('/comments/section/{sectionId}', {
        params: { path: { sectionId: sectionSuperId } },
        body: { data: comment as CommentData },
      });
      return data;
    },
  });

  const deleteComment = useMutation({
    mutationFn: async (commentId: string) => {
      await api.DELETE('/comments/{commentId}', { params: { path: { commentId } } });
    },

    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: commentKeys.list(sectionSuperId),
      });
    },
  });

  const updateCommentMessage = useMutation({
    mutationFn: async ({ commentId, message }: { commentId: string; message: string }) => {
      await api.PUT('/comments/{commentId}', { params: { path: { commentId } }, body: { message } });
    },
  });

  const updateCommentStatus = useMutation({
    mutationFn: async ({ commentId, status }: { commentId: string; status: boolean }) => {
      await api.PUT('/comments/{commentId}/status', { params: { path: { commentId } }, body: { status } });
    },

    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: commentKeys.list(sectionSuperId),
      });
    },
  });

  const publishComment = useMutation({
    mutationFn: async ({ commentId, publish }: { commentId: string; publish: boolean }) => {
      await api.PUT('/comments/{commentId}/publish', { params: { path: { commentId } }, body: { publish } });
    },

    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: commentKeys.list(sectionSuperId),
      });
    },
  });

  const deleteCommentConnection = useMutation({
    mutationFn: async (commentId: string) => {
      await api.DELETE('/comments/{commentId}/connection', { params: { path: { commentId } } });
    },

    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: commentKeys.list(sectionSuperId),
      });
    },
  });

  const restoreCommentConnection = useMutation({
    mutationFn: async (commentId: string) => {
      await api.PUT('/comments/{commentId}/connection', { params: { path: { commentId } }, body: {} });
    },

    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: commentKeys.list(sectionSuperId),
      });
    },
  });

  const { data: commentNotifications, isLoading: isCommentNotificationsLoading } = useQuery({
    queryKey: commentKeys.notifications(sectionSuperId),

    queryFn: async () => {
      const { data } = await api.GET('/comments/notifications');
      return data;
    },
  });

  return {
    comments,
    isCommentsLoading,

    refetchComments: refetch,

    createComment: createComment.mutate,
    createCommentAsync: createComment.mutateAsync,

    deleteComment: deleteComment.mutate,
    updateCommentMessage: updateCommentMessage.mutate,
    updateCommentMessageAsync: updateCommentMessage.mutateAsync,

    updateCommentStatus: updateCommentStatus.mutate,
    publishComment: publishComment.mutate,

    deleteCommentConnection: deleteCommentConnection.mutate,
    restoreCommentConnection: restoreCommentConnection.mutate,

    commentNotifications,
    isCommentNotificationsLoading,
  };
};
